import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Avatar } from './Avatar';

/** Le bandeau d'une fiche : dégradé doux, avatar, nom, pastilles, actions — puis les onglets. */
export function Bandeau({
  retour,
  nom,
  logo,
  onLogo,
  sousTitre,
  pastilles,
  actions,
  children,
}: {
  retour: { vers: string; libelle: string };
  nom: string;
  /** Un logo à la place des initiales : contenu entier, sur fond blanc. */
  logo?: string | null | undefined;
  /** Un clic sur le rond : l'ajuster (fiche partenaire). */
  onLogo?: (() => void) | undefined;
  sousTitre: ReactNode;
  pastilles?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card">
      <div className="flex flex-wrap items-start gap-4 bg-brand-soft px-6 pt-4 pb-5">
        <div className="min-w-0 flex-1">
          <Link
            to={retour.vers}
            className="mb-3.5 inline-flex items-center gap-1.5 text-[13px] font-medium"
          >
            <ArrowLeft className="size-4" aria-hidden />
            {retour.libelle}
          </Link>
          <div className="flex items-center gap-4">
            {onLogo ? (
              <button
                type="button"
                onClick={onLogo}
                title={logo ? 'Ajuster le logo' : 'Ajouter un logo'}
                aria-label={logo ? 'Ajuster le logo' : 'Ajouter un logo'}
                className="shrink-0 rounded-full transition hover:ring-3 hover:ring-primary-subtle focus-visible:outline-2 focus-visible:outline-primary"
              >
                <Avatar
                  nom={nom}
                  photo={logo}
                  className={
                    logo ? 'size-16 bg-white object-contain p-1.5' : 'size-16 bg-white/70 text-xl'
                  }
                />
              </button>
            ) : (
              <Avatar
                nom={nom}
                photo={logo}
                className={
                  logo ? 'size-16 bg-white object-contain p-1.5' : 'size-16 bg-white/70 text-xl'
                }
              />
            )}
            <div className="min-w-0">
              <h1 className="text-[28px] leading-tight font-bold">{nom}</h1>
              <div className="mt-0.5 mb-2.5 flex flex-wrap items-center gap-1.5 text-muted">
                {sousTitre}
              </div>
              {pastilles ? <div className="flex flex-wrap gap-2">{pastilles}</div> : null}
            </div>
          </div>
        </div>
        {actions ? <div className="flex gap-2 self-center">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Pastille({ icone, children }: { icone?: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-primary-border bg-white/75 px-2.5 py-1 text-xs font-medium [&_svg]:size-3.5 [&_svg]:text-primary">
      {icone}
      {children}
    </span>
  );
}
